import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/shared/components/Layout';
import { useRpgStore } from '@/stores/rpgStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { PaywallModal } from '@/features/subscription/PaywallModal';
import { api } from '@/shared/lib/api';
import { trackEvent, captureError } from '@/shared/lib/monitoring';
import { useBackHandler } from '@/shared/lib/navigation/BackNavigationContext';
import { useOnTabActive, useIsTabActive } from '@/shared/components/MainTabsKeepAlive';

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#10b981',
  rare: '#3b82f6',
  epic: '#a855f7',
  legendary: '#f59e0b',
};

const RARITY_NAMES_RU: Record<string, string> = {
  common: 'Обычный',
  uncommon: 'Необычный',
  rare: 'Редкий',
  epic: 'Эпический',
  legendary: 'Легендарный',
};

const RARITY_SCORE_MAP: Record<string, number> = {
  common: 10,
  uncommon: 25,
  rare: 50,
  epic: 100,
  legendary: 250,
};

const STAT_ICONS: Record<string, string> = {
  strength: '💪',
  dexterity: '⚡',
  intelligence: '🧠',
  luck: '🍀',
  humor: '😄',
};

const STAT_NAMES_RU: Record<string, string> = {
  strength: 'Сила',
  dexterity: 'Ловкость',
  intelligence: 'Интеллект',
  luck: 'Удача',
  humor: 'Юмор',
};

const SLOT_ICONS: Record<string, string> = {
  HEAD: '🪖',
  BODY: '🛡️',
  HANDS: '🧤',
  FEET: '👢',
  RING: '💍',
  AMULET: '📿',
  WEAPON: '⚔️',
};

const SLOT_NAMES_RU: Record<string, string> = {
  HEAD: 'Шлем',
  BODY: 'Доспех',
  HANDS: 'Перчатки',
  FEET: 'Сапоги',
  RING: 'Кольцо',
  AMULET: 'Амулет',
  WEAPON: 'Оружие',
};

type Tab = 'character' | 'inventory' | 'subscription';
type InvFilter = 'all' | 'weapon' | 'armor' | 'jewelry';

interface PaperdollSlotProps {
  slot: string;
  equipped?: any;
  onClick: () => void;
}

function PaperdollSlot({ slot, equipped, onClick }: PaperdollSlotProps) {
  const isEquipped = Boolean(equipped && equipped.item_def);
  const rarity = equipped?.item_def?.rarity || 'common';
  const rarityColor = RARITY_COLORS[rarity] || '#9e9e9e';

  return (
    <div
      onClick={onClick}
      style={{
        width: '100%',
        minHeight: 74,
        background: isEquipped
          ? `linear-gradient(180deg, ${rarityColor}18 0%, rgba(15, 15, 26, 0.7) 100%)`
          : 'rgba(255, 255, 255, 0.03)',
        border: isEquipped
          ? `2px solid ${rarityColor}`
          : '1.5px dashed var(--theme-border)',
        borderRadius: 14,
        padding: '6px 4px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        position: 'relative',
        boxShadow: isEquipped ? `0 0 12px ${rarityColor}28` : 'none',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ fontSize: 24, lineHeight: 1, marginBottom: 4 }}>
        {SLOT_ICONS[slot]}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: isEquipped ? 700 : 500,
          color: isEquipped ? rarityColor : 'var(--theme-text-secondary)',
          maxWidth: 72,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.2,
        }}
      >
        {isEquipped ? (equipped.item_def.name_ru || equipped.item_def.name) : SLOT_NAMES_RU[slot]}
      </div>
      {isEquipped && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: rarityColor,
            boxShadow: `0 0 4px ${rarityColor}`,
          }}
        />
      )}
    </div>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const { character, isLoaded, load, equip, unequip, rename } = useRpgStore();
  const { plan, is_premium, expires_at, isLoaded: subLoaded, load: loadSub } = useSubscriptionStore();
  const [tab, setTab] = useState<Tab>('character');
  const [invFilter, setInvFilter] = useState<InvFilter>('all');
  const [showPaywall, setShowPaywall] = useState(false);
  const [renamingMode, setRenamingMode] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [referralInfo, setReferralInfo] = useState<{ referral_code: string; referrals_count: number; referred_by: string | null } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3200);
  };

  const handleSlotClick = (slotKey: string) => {
    const eq = character?.equipped[slotKey];
    if (eq) {
      setSelectedItem({ ...eq, actionType: 'unequip' });
    } else {
      showToast(`Слот «${SLOT_NAMES_RU[slotKey] || slotKey}» пуст. Снаряжение выпадает из сундуков 🎁 за уроки курса!`);
    }
  };

  const isTabActive = useIsTabActive();

  useEffect(() => {
    load();
    if (!subLoaded) loadSub();
  }, []);

  // Фоновое тихое обновление персонажа и подписки при возвращении на вкладку
  useOnTabActive(() => {
    load();
    loadSub();
    if (tab === 'subscription') {
      api.get<any>('/users/referral').then(setReferralInfo).catch((err) => captureError(err, { context: 'ProfilePage.referralInfo' }));
    }
  });

  // Закрытие модалок и возврат на первую вкладку по свайпу назад / Telegram BackButton
  useBackHandler(() => {
    if (selectedItem) {
      setSelectedItem(null);
    } else if (showPaywall) {
      setShowPaywall(false);
    } else if (renamingMode) {
      setRenamingMode(false);
    } else if (tab !== 'character') {
      setTab('character');
    }
  }, isTabActive && Boolean(selectedItem || showPaywall || renamingMode || tab !== 'character'), 5);

  useEffect(() => {
    if (tab === 'subscription') {
      api.get<any>('/users/referral').then(setReferralInfo).catch((err) => captureError(err, { context: 'ProfilePage.referralInfo' }));
    }
  }, [tab]);

  const botUsername = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? 'SecondEnBot';
  const referralLink = referralInfo ? `https://t.me/${botUsername}?start=${referralInfo.referral_code}` : '';

  const handleCopyReferral = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink).then(() => {
        setCopiedLink(true);
        trackEvent('referral_link_copied');
        setTimeout(() => setCopiedLink(false), 2000);
      });
    }
  };

  const handleRename = async () => {
    if (!newName.trim()) return;
    await rename(newName.trim());
    setRenamingMode(false);
  };

  const handleEquip = async (itemId: number) => {
    await equip(itemId);
    setSelectedItem(null);
  };

  const handleUnequip = async (itemId: number) => {
    await unequip(itemId);
    setSelectedItem(null);
  };

  if (!isLoaded) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: 'var(--theme-text)' }}>
          Загрузка...
        </div>
      </Layout>
    );
  }

  const xpPercent = character
    ? Math.min(100, Math.round((character.xp / character.xp_to_next_level) * 100))
    : 0;

  // Рассчитываем сумму бонусов со всей экипировки
  const equipmentBonuses = character
    ? Object.values(character.equipped).reduce(
        (acc, eq) => {
          if (!eq?.item_def) return acc;
          return {
            strength: acc.strength + (eq.item_def.str_bonus || 0),
            dexterity: acc.dexterity + (eq.item_def.dex_bonus || 0),
            intelligence: acc.intelligence + (eq.item_def.int_bonus || 0),
            luck: acc.luck + (eq.item_def.luck_bonus || 0),
            humor: acc.humor + (eq.item_def.humor_bonus || 0),
          };
        },
        { strength: 0, dexterity: 0, intelligence: 0, luck: 0, humor: 0 }
      )
    : { strength: 0, dexterity: 0, intelligence: 0, luck: 0, humor: 0 };

  const totalBonusesCount = Object.values(equipmentBonuses).reduce((a, b) => a + b, 0);

  // Очки мощи экипировки (Gear Score)
  const gearScore = character
    ? Object.values(character.equipped).reduce((score, eq: any) => {
        if (!eq?.item_def) return score;
        const rarity = eq.item_def.rarity || 'common';
        const rarityPts = RARITY_SCORE_MAP[rarity] || 10;
        const statsPts =
          ((eq.item_def.str_bonus || 0) +
            (eq.item_def.dex_bonus || 0) +
            (eq.item_def.int_bonus || 0) +
            (eq.item_def.luck_bonus || 0) +
            (eq.item_def.humor_bonus || 0)) *
          5;
        return score + rarityPts + statsPts;
      }, 0)
    : 0;

  // Фильтрация инвентаря
  const inventoryItems = character?.inventory || [];
  const filteredInventory = inventoryItems.filter(item => {
    const slot = item.item_def?.slot;
    if (invFilter === 'weapon') return slot === 'WEAPON';
    if (invFilter === 'armor') return ['HEAD', 'BODY', 'HANDS', 'FEET'].includes(slot);
    if (invFilter === 'jewelry') return ['RING', 'AMULET'].includes(slot);
    return true;
  });

  return (
    <Layout>
      <div style={{ padding: '16px', color: 'var(--theme-text)', paddingBottom: 80, maxWidth: 480, margin: '0 auto' }}>

        {/* Герой и Уровень */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          {renamingMode ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                maxLength={30}
                placeholder="Имя персонажа"
                autoFocus
                style={{
                  background: 'var(--theme-bg)', color: 'var(--theme-text)',
                  border: '1px solid var(--theme-primary)', borderRadius: 10,
                  padding: '8px 12px', fontSize: 16, maxWidth: 200,
                }}
              />
              <button onClick={handleRename} style={{
                background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
                border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700,
              }}>✓</button>
              <button onClick={() => setRenamingMode(false)} style={{
                background: 'var(--theme-bg)', color: 'var(--theme-text-secondary)',
                border: '1px solid var(--theme-border)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
              }}>✕</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{character?.name ?? 'Language Adventurer'}</div>
              <button onClick={() => { setNewName(character?.name ?? ''); setRenamingMode(true); }} style={{
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 15,
                color: 'var(--theme-text-secondary)', padding: 4,
              }}>✏️</button>
            </div>
          )}

          {character && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.03em' }}>
                Уровень {character.level} · Маг Слов
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(124, 77, 255, 0.25) 100%)',
                border: '1px solid rgba(168, 85, 247, 0.35)',
                borderRadius: 20,
                padding: '4px 12px',
                boxShadow: '0 2px 8px rgba(124, 77, 255, 0.2)',
              }}>
                <span style={{ fontSize: 12, color: '#c084fc', fontWeight: 800 }}>
                  ⚔️ Мощь снаряжения: {gearScore} PTS
                </span>
              </div>
            </div>
          )}
        </div>

        {/* XP Bar */}
        {character && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 5 }}>
              <span>Опыт персонажа</span>
              <span style={{ fontWeight: 600 }}>{character.xp} / {character.xp_to_next_level} XP</span>
            </div>
            <div style={{ background: 'var(--theme-bg-elevated)', borderRadius: 8, height: 10, overflow: 'hidden', border: '1px solid var(--theme-border)' }}>
              <div style={{
                width: `${xpPercent}%`, height: '100%',
                background: 'linear-gradient(90deg, #f59e0b, #10b981)',
                transition: 'width 0.5s ease',
                boxShadow: '0 0 8px rgba(245, 158, 11, 0.4)',
              }} />
            </div>
          </div>
        )}

        {/* Табы */}
        <div style={{
          display: 'flex', background: 'var(--theme-bg-elevated)',
          borderRadius: 14, padding: 4, marginBottom: 18,
          border: '1px solid var(--theme-border)',
        }}>
          {([['character', '⚔️ Персонаж'], ['inventory', `🎒 Инвентарь (${inventoryItems.length})`], ['subscription', '⭐ Подписка']] as [Tab, string][]).map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: '10px 4px', border: 'none', borderRadius: 10, cursor: 'pointer',
              background: tab === t ? 'var(--theme-primary)' : 'transparent',
              color: tab === t ? 'var(--theme-primary-text)' : 'var(--theme-text-secondary)',
              fontWeight: tab === t ? 700 : 500, fontSize: 13,
              transition: 'all 0.2s',
            }}>{label}</button>
          ))}
        </div>

        {/* Вкладка: Персонаж (Кукла снаряжения) */}
        {tab === 'character' && character && (
          <div>
            {/* 3-Колоночная сцена экипировки (Paperdoll) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '80px 1fr 80px',
              gap: 10,
              alignItems: 'center',
              background: 'linear-gradient(180deg, rgba(30, 27, 75, 0.35) 0%, rgba(15, 15, 26, 0.6) 100%)',
              border: '1px solid rgba(124, 77, 255, 0.25)',
              borderRadius: 20,
              padding: '16px 10px',
              marginBottom: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}>
              {/* Левая колонка: Оружие, Кольцо */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <PaperdollSlot
                  slot="WEAPON"
                  equipped={character.equipped['WEAPON']}
                  onClick={() => handleSlotClick('WEAPON')}
                />
                <PaperdollSlot
                  slot="RING"
                  equipped={character.equipped['RING']}
                  onClick={() => handleSlotClick('RING')}
                />
              </div>

              {/* Центр: Голова, Аватар героя, Доспех, Сапоги */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 80 }}>
                  <PaperdollSlot
                    slot="HEAD"
                    equipped={character.equipped['HEAD']}
                    onClick={() => handleSlotClick('HEAD')}
                  />
                </div>

                {/* Центральный пьедестал героя */}
                <div style={{
                  position: 'relative',
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(124, 77, 255, 0.25) 0%, rgba(15, 15, 26, 0.1) 70%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(124, 77, 255, 0.3)',
                  boxShadow: '0 0 20px rgba(124, 77, 255, 0.2)',
                }}>
                  <div style={{ fontSize: 48, filter: 'drop-shadow(0 4px 10px rgba(124, 77, 255, 0.5))' }}>
                    🧙‍♂️
                  </div>
                  {totalBonusesCount > 0 && (
                    <div style={{
                      position: 'absolute',
                      bottom: -6,
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      color: '#ffffff',
                      fontSize: 10,
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 999,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                      whiteSpace: 'nowrap',
                    }}>
                      +{totalBonusesCount} к статам
                    </div>
                  )}
                </div>

                <div style={{ width: 80 }}>
                  <PaperdollSlot
                    slot="BODY"
                    equipped={character.equipped['BODY']}
                    onClick={() => handleSlotClick('BODY')}
                  />
                </div>

                <div style={{ width: 80 }}>
                  <PaperdollSlot
                    slot="FEET"
                    equipped={character.equipped['FEET']}
                    onClick={() => handleSlotClick('FEET')}
                  />
                </div>
              </div>

              {/* Правая колонка: Амулет, Перчатки */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <PaperdollSlot
                  slot="AMULET"
                  equipped={character.equipped['AMULET']}
                  onClick={() => handleSlotClick('AMULET')}
                />
                <PaperdollSlot
                  slot="HANDS"
                  equipped={character.equipped['HANDS']}
                  onClick={() => handleSlotClick('HANDS')}
                />
              </div>
            </div>

            {/* Характеристики героя с экипировкой */}
            <div style={{
              background: 'var(--theme-bg-elevated)', borderRadius: 16, padding: '16px',
              border: '1px solid var(--theme-border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>Характеристики</div>
                <div style={{ fontSize: 12, color: '#c084fc', fontWeight: 700 }}>
                  ⚔️ {gearScore} PTS {totalBonusesCount > 0 ? `(+${totalBonusesCount})` : ''}
                </div>
              </div>

              {Object.entries(character.stats).map(([stat, baseVal]) => {
                const bonus = (equipmentBonuses as any)[stat] || 0;
                const total = (baseVal as number) + bonus;

                return (
                  <div key={stat} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 10,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{STAT_ICONS[stat]}</span>
                      <span>{STAT_NAMES_RU[stat] || stat}</span>
                    </span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {Array.from({ length: 12 }).map((_, i) => (
                          <div key={i} style={{
                            width: 7, height: 8, borderRadius: 2,
                            background: i < (baseVal as number)
                              ? 'var(--theme-primary)'
                              : i < total
                                ? '#10b981'
                                : 'rgba(255,255,255,0.08)',
                          }} />
                        ))}
                      </div>

                      <div style={{ fontSize: 13, minWidth: 44, textAlign: 'right', fontWeight: 700 }}>
                        <span>{total}</span>
                        {bonus > 0 && (
                          <span style={{ color: '#10b981', fontSize: 11, marginLeft: 3 }}>
                            (+{bonus})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Вкладка: Инвентарь */}
        {tab === 'inventory' && (
          <div>
            {/* Фильтры категорий */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
              {([
                ['all', 'Все'],
                ['weapon', 'Оружие'],
                ['armor', 'Доспехи'],
                ['jewelry', 'Бижутерия'],
              ] as [InvFilter, string][]).map(([f, label]) => (
                <button
                  key={f}
                  onClick={() => setInvFilter(f)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    border: invFilter === f ? '1px solid var(--theme-primary)' : '1px solid var(--theme-border)',
                    background: invFilter === f ? 'rgba(124, 77, 255, 0.15)' : 'var(--theme-bg-elevated)',
                    color: invFilter === f ? 'var(--theme-primary)' : 'var(--theme-text-secondary)',
                    fontSize: 12,
                    fontWeight: invFilter === f ? 700 : 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {filteredInventory.length === 0 ? (
              <div style={{
                textAlign: 'center', color: 'var(--theme-text-secondary)',
                padding: '40px 20px', background: 'var(--theme-bg-elevated)',
                borderRadius: 16, border: '1px solid var(--theme-border)',
              }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🎒</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--theme-text)' }}>
                  {invFilter === 'all' ? 'Инвентарь пуст' : 'В этой категории пусто'}
                </div>
                <div style={{ fontSize: 13, marginTop: 6, maxWidth: 280, marginInline: 'auto' }}>
                  Учите слова и проходите уроки — из сундуков и за серии ответов падает редкий лут!
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {filteredInventory.map(item => {
                  const rarity = item.item_def.rarity || 'common';
                  const rarityColor = RARITY_COLORS[rarity] || '#9e9e9e';

                  return (
                    <div
                      key={item.id}
                      onClick={() => setSelectedItem({ ...item, actionType: item.is_equipped ? 'unequip' : 'equip' })}
                      style={{
                        background: 'var(--theme-bg-elevated)',
                        border: item.is_equipped
                          ? `2px solid var(--theme-primary)`
                          : `1px solid ${rarityColor}55`,
                        borderRadius: 14,
                        padding: '12px 6px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        boxShadow: item.is_equipped
                          ? '0 0 12px rgba(124, 77, 255, 0.3)'
                          : `0 2px 8px ${rarityColor}15`,
                        transition: 'transform 0.15s ease',
                      }}
                    >
                      {item.is_equipped && (
                        <div style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          fontSize: 9,
                          fontWeight: 800,
                          background: 'var(--theme-primary)',
                          color: 'var(--theme-primary-text)',
                          borderRadius: 4,
                          padding: '1px 5px',
                        }}>
                          НАДЕТО
                        </div>
                      )}

                      <div style={{ fontSize: 32, marginBottom: 4 }}>
                        {SLOT_ICONS[item.item_def.slot] ?? '📦'}
                      </div>

                      <div style={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: rarityColor,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        marginBottom: 2,
                      }}>
                        {RARITY_NAMES_RU[rarity] || rarity}
                      </div>

                      <div style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--theme-text)',
                        lineHeight: 1.2,
                        marginBottom: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: 28,
                      }}>
                        {item.item_def.name_ru || item.item_def.name}
                      </div>

                      {/* Превью статов */}
                      {(() => {
                        const topBonus = [
                          { icon: '💪', val: item.item_def.str_bonus },
                          { icon: '⚡', val: item.item_def.dex_bonus },
                          { icon: '🧠', val: item.item_def.int_bonus },
                          { icon: '🍀', val: item.item_def.luck_bonus },
                          { icon: '😄', val: item.item_def.humor_bonus },
                        ].find(b => b.val > 0);

                        return topBonus ? (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: 6,
                            padding: '1px 6px',
                          }}>
                            {topBonus.icon} +{topBonus.val}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Вкладка: Подписка */}
        {tab === 'subscription' && (
          <div>
            <div style={{
              background: is_premium ? '#FFD70022' : 'var(--theme-bg-elevated)',
              border: is_premium ? '1px solid #FFD700' : '1px solid var(--theme-border)',
              borderRadius: 16, padding: '20px 16px', textAlign: 'center', marginBottom: 16,
            }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>{is_premium ? '⭐' : '🔓'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                {is_premium ? 'Premium' : 'Free'}
              </div>
              {is_premium && expires_at && (
                <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
                  Действует до {new Date(expires_at).toLocaleDateString('ru-RU')}
                </div>
              )}
            </div>

            {!is_premium && (
              <button onClick={() => setShowPaywall(true)} style={{
                width: '100%', padding: '16px',
                background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
                border: 'none', borderRadius: 12,
                fontSize: 16, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
              }}>
                Купить Premium ⭐
              </button>
            )}

            <div style={{ marginTop: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Возможности Free vs Premium</div>
              {[
                ['6 режимов обучения', true, true],
                ['Все курсы (A1–C2)', false, true],
                ['Безлимитный AI', false, true],
                ['Все 8 тем', false, true],
                ['Грамматика', false, true],
                ['RPG предметы', false, true],
              ].map(([feat, free, premium]) => (
                <div key={String(feat)} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: '1px solid var(--theme-border)',
                  fontSize: 14,
                }}>
                  <span style={{ color: 'var(--theme-text)' }}>{String(feat)}</span>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <span>{free ? '✅' : '❌'}</span>
                    <span>{premium ? '✅' : '❌'}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Реферальная программа */}
            <div style={{
              background: 'var(--theme-bg-elevated)', borderRadius: 16,
              padding: '16px', marginTop: 20, border: '1px solid var(--theme-border)',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 16 }}>
                🎁 Пригласи друга
              </div>
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 12 }}>
                Ты и твой друг получите по 7 дней Premium бесплатно!
                {referralInfo?.referrals_count !== undefined && referralInfo.referrals_count > 0 && (
                  <span> · Приглашено: <b>{referralInfo.referrals_count}</b></span>
                )}
              </div>
              {referralLink && (
                <button onClick={handleCopyReferral} style={{
                  width: '100%', padding: '12px',
                  background: copiedLink ? '#44bb44' : 'var(--theme-primary)',
                  color: 'var(--theme-primary-text)', border: 'none', borderRadius: 10,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  transition: 'background 0.2s',
                }}>
                  {copiedLink ? '✅ Ссылка скопирована!' : '📋 Скопировать реферальную ссылку'}
                </button>
              )}
              {referralInfo?.referred_by && (
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 8, textAlign: 'center' }}>
                  Тебя пригласил: {referralInfo.referred_by}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Модальное окно детализации предмета */}
        {selectedItem && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            zIndex: 200, display: 'flex', alignItems: 'flex-end',
          }} onClick={() => setSelectedItem(null)}>
            <div style={{
              background: 'var(--theme-bg-elevated)',
              borderRadius: '24px 24px 0 0', padding: '24px 20px 40px',
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
              borderTop: `2px solid ${RARITY_COLORS[selectedItem.item_def.rarity] || 'var(--theme-border)'}`,
              boxShadow: '0 -8px 30px rgba(0,0,0,0.5)',
            }} onClick={e => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 52, marginBottom: 6 }}>
                  {SLOT_ICONS[selectedItem.item_def.slot] ?? '📦'}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
                  color: RARITY_COLORS[selectedItem.item_def.rarity] || '#9e9e9e',
                  marginBottom: 6,
                }}>
                  {RARITY_NAMES_RU[selectedItem.item_def.rarity]?.toUpperCase() || selectedItem.item_def.rarity.toUpperCase()} · {SLOT_NAMES_RU[selectedItem.item_def.slot] || selectedItem.item_def.slot}
                </div>
                <div style={{
                  fontSize: 20, fontWeight: 800, color: 'var(--theme-text)',
                  marginBottom: 2,
                }}>
                  {selectedItem.item_def.name_ru || selectedItem.item_def.name}
                </div>
                {selectedItem.item_def.name_ru && selectedItem.item_def.name && (
                  <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 8 }}>
                    {selectedItem.item_def.name}
                  </div>
                )}
              </div>

              {/* Бонусы характеристик */}
              {(() => {
                const bonuses = [
                  { key: 'str', icon: '💪', label: 'Сила', value: selectedItem.item_def.str_bonus },
                  { key: 'dex', icon: '⚡', label: 'Ловкость', value: selectedItem.item_def.dex_bonus },
                  { key: 'int', icon: '🧠', label: 'Интеллект', value: selectedItem.item_def.int_bonus },
                  { key: 'luck', icon: '🍀', label: 'Удача', value: selectedItem.item_def.luck_bonus },
                  { key: 'hmr', icon: '😄', label: 'Юмор', value: selectedItem.item_def.humor_bonus },
                ].filter(b => b.value > 0);

                return bonuses.length > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {bonuses.map(b => (
                      <div key={b.key} style={{
                        background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: 10, padding: '6px 12px', fontSize: 13, fontWeight: 700, color: '#10b981',
                      }}>
                        {b.icon} +{b.value} {b.label}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}

              {selectedItem.item_def.description_ru && (
                <div style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--theme-text)', textAlign: 'center', marginBottom: 6 }}>
                  «{selectedItem.item_def.description_ru}»
                </div>
              )}
              {selectedItem.item_def.description && (
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', textAlign: 'center', marginBottom: 20 }}>
                  {selectedItem.item_def.description}
                </div>
              )}

              {selectedItem.actionType === 'equip' ? (
                <button onClick={() => handleEquip(selectedItem.id)} style={{
                  width: '100%', padding: '16px',
                  background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
                  border: 'none', borderRadius: 14,
                  fontSize: 16, fontWeight: 800, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                }}>
                  <span>⚔️</span>
                  <span>Надеть в слот «{SLOT_NAMES_RU[selectedItem.item_def.slot]}»</span>
                </button>
              ) : (
                <button onClick={() => handleUnequip(selectedItem.id)} style={{
                  width: '100%', padding: '16px',
                  background: 'var(--theme-bg)', color: 'var(--theme-text)',
                  border: '1px solid var(--theme-border)', borderRadius: 14,
                  fontSize: 16, fontWeight: 700, cursor: 'pointer',
                }}>
                  🎒 Снять в инвентарь
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}

      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: 84,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(168, 85, 247, 0.4)',
          color: '#ffffff',
          padding: '10px 18px',
          borderRadius: 14,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          maxWidth: '90%',
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          <span>ℹ️</span>
          <span>{toastMessage}</span>
        </div>
      )}
    </Layout>
  );
}
